import type { ActionView } from './action-confirmation';
import { actionCopy, type ActionCopy, type ActionKind } from './action-copy';
import { privateActionField } from './action-field-privacy';
import { WORK_CONNECTION_CHANGES } from './settings-actions-copy';
import { useChatCopy, useChatInterfaceLocale, type ChatCopy } from '@/lib/chat-i18n';
import { formatCopy, namespaceCopyFor, useNamespaceCopy } from '@/i18n/ns';
import appApprovalsCopy, { type AppApprovalsCopy } from '@/i18n/ns/app-approvals';
import meetingsKnowledgeCopy from '@/i18n/ns/meetings-knowledge';
import storefrontCopy from '@/i18n/ns/storefront';
import appTrackerCopy, { type AppTrackerCopyKey } from '@/i18n/ns/app-tracker';
import { parseAppSchedulePresentation } from '@/workspace/apps/schedule-presentation';
import {
  formatMoment,
  laterRunWords,
  scheduleWords,
} from '@/workspace/apps/tracker/schedule-words';
import { readTitle } from '@/workspace/storefront/read-request';
import { MODEL_CHOICE_COPY, type ModelChoice } from '@/workspace/model-choices.generated';

const ENGLISH_WORDS = namespaceCopyFor(appApprovalsCopy, 'en');

/** Exact server action types and the fields that are safe and useful to show. */
export const ACTION_FIELDS: Record<string, string[]> = {
  add_meeting_note: ['content'],
  create_skill_draft: [
    'name',
    'description',
    'instructions',
    'modelChoice',
    'fileIds',
    'sourceIds',
  ],
  update_skill_draft: [
    'id',
    'name',
    'description',
    'instructions',
    'modelChoice',
    'fileIds',
    'sourceIds',
    'revision',
  ],
  create_agent: [
    'name',
    'goal',
    'instructions',
    'expectedOutput',
    'resources',
    'boundaries',
    'skillIds',
    'schedule',
    'enabled',
    'scope',
  ],
  update_agent: [
    'id',
    'goal',
    'instructions',
    'expectedOutput',
    'resources',
    'boundaries',
    'skillIds',
  ],
  set_agent_trigger: ['id', 'schedule', 'enabled'],
  // Projects v1: a change every chat in the project will read.
  update_project: ['project', 'name', 'brief', 'endsOn', 'followsSkills', 'readsFirst'],
  run_agent: ['id'],
  update_my_settings: [
    'theme',
    'language',
    'timezone',
    'chat_model_choice',
    'personality',
    'voice_prefs',
  ],
  save_memory: ['content'],
  update_memory: ['content'],
  forget_memory: ['content'],
  propose_company_knowledge: ['action', 'content', 'category', 'source'],
  propose_knowledge_change: ['kind', 'pageId', 'title', 'markdown', 'reason', 'evidence'],
  // The card names the page in words and shows its edits as a diff
  // (page-suggestion-view.ts); `markdown` is a new page's text, which the Draft
  // tab lifts out of its replace_page edit.
  suggest_page_edits: ['title', 'reason', 'markdown'],
  save_work_result: ['title', 'kind', 'payload', 'replaces', 'baseRevision', 'sourceIds'],
  // The native build: the exact reviewed request text and nothing else (no model/kind/company).
  build_artifact: ['task'],
  set_chat_model: ['choice'],
  // The item, the change, the connection by name and what it reads in words;
  // ids stay off the card.
  update_work_connection: [
    'title',
    'change',
    'connection',
    'reads',
    'resourceLabels',
    'allowChanges',
  ],
  withdraw_pending_proposal: [],
  // The App by name, the services in words and what it may do there; the App id stays off the card.
  approve_app_outside_access: ['title', 'hosts', 'access'],
  // The App, the event and what it runs, in words; the App and event ids stay off the card.
  set_app_event: ['title', 'event', 'kind', 'collection', 'changes', 'runs', 'enabled'],
  // The App, the job, what it runs, its exact cron and zone and the state it is left in; ids stay off the card.
  set_app_schedule: ['title', 'job', 'runs', 'schedule', 'timezone', 'state'],
  // The App, the function, its tables, the changes and for whom, in words; ids stay off the card.
  set_app_data_writes: ['title', 'function', 'collections', 'changes', 'who', 'allowed'],
  // The App, the action and the company system it writes to; connection and function ids stay off.
  set_app_connected_write: ['title', 'action', 'destination', 'who', 'enabled'],
  // The App and who changed its code; the App id stays off the card.
  confirm_app_code: ['title', 'changedBy'],
  // The App, the function and the exact input it runs with; the App id stays off the card.
  run_app_function_with_changes: ['title', 'function', 'input'],
  // The App by name, the change, and (from the saved version) what becomes public.
  publish_app_public_link: [
    'title',
    'change',
    'publicTables',
    'publicActions',
    'publicRuns',
    'publicRunsOff',
    'embedSites',
  ],
  // A company admin's takedown: the App by name, its public address and who published it.
  unpublish_company_public_link: ['title', 'publicLink', 'publishedBy'],
  // The App, the change, the table, the field and the rows it changes, in words; ids stay off the card.
  apply_app_table_change: ['title', 'change', 'restores', 'table', 'field', 'rows', 'keptDays'],
};

const listed = (value: unknown) =>
  Array.isArray(value) && value.length ? value.map(String).join(', ') : null;
type Present = (value: unknown) => unknown;
/** What each card's values read as, in the card's language: wire values become words, never ids. */
function valueTables(words: AppApprovalsCopy): Record<string, Record<string, Present>> {
  const pick =
    (names: Record<string, string>): Present =>
    (value) =>
      names[String(value)] ?? value;
  const yesNo: Present = (value) => (value === true ? words.answerYes : words.answerNo);
  const changes: Present = (value) =>
    Array.isArray(value) && value.length
      ? value.map((item) => pick(EVENT_CHANGES(words))(item)).join(', ')
      : null;
  const who = pick({ owner: words.whoOwner, everyone: words.whoEveryone });
  const onOff: Present = (value) => (value === true ? words.switchOn : words.switchOff);
  const tableChange = pick({
    drop_field: words.wordDropField,
    drop_table: words.wordDropTable,
    clear_values: words.tableClearValues,
    restore: words.tableRestore,
  });
  const event: Record<string, Present> = {
    kind: pick({ webhook: words.eventWebhook, collection: words.eventRows }),
    changes,
    runs: (value) => spokenFunction(value),
    enabled: onOff,
  };
  return {
    update_work_connection: {
      change: (value) => WORK_CONNECTION_CHANGES[String(value)] ?? value,
      reads: listed,
      resourceLabels: listed,
      allowChanges: yesNo,
    },
    approve_app_outside_access: {
      hosts: listed,
      access: pick({
        read: words.wordRead,
        read_send: words.wordReadSend,
        none: words.wordNoAccess,
      }),
    },
    set_app_event: event,
    set_app_schedule: {
      runs: (value) => spokenFunction(value),
      state: pick({ on: words.switchOn, paused: words.switchPaused }),
    },
    set_app_data_writes: {
      collections: listed,
      changes,
      who,
      allowed: yesNo,
      function: (value) => spokenFunction(value),
    },
    set_app_connected_write: { who, enabled: onOff },
    confirm_app_code: { changedBy: listed },
    run_app_function_with_changes: {
      function: (value) => spokenFunction(value),
      input: (value) => runInput(value),
    },
    publish_app_public_link: {
      change: pick({
        publish: words.publicPublish,
        refresh: words.publicRefresh,
        unpublish: words.publicUnpublish,
      }),
      publicTables: listed,
      publicActions: listed,
      publicRuns: listed,
      publicRunsOff: listed,
      embedSites: listed,
    },
    apply_app_table_change: { change: tableChange, restores: tableChange },
  };
}
const EVENT_CHANGES = (words: AppApprovalsCopy): Record<string, string> => ({
  insert: words.changeInsert,
  update: words.changeUpdate,
  delete: words.changeDelete,
});
/** The exact input a run is approved with; a long one is cut with an ellipsis, never silently. */
const INPUT_SHOWN = 600;
const runInput = (value: unknown) => {
  if (value === undefined) return '{}';
  const text = JSON.stringify(value);
  return text.length > INPUT_SHOWN ? `${text.slice(0, INPUT_SHOWN - 1)}…` : text;
};
const presented = (action: ActionView, key: string, words: AppApprovalsCopy) => {
  const tables = valueTables(words);
  const present = Object.hasOwn(tables, action.tool) ? tables[action.tool][key] : undefined;
  return present ? present(action.args[key]) : action.args[key];
};

/** How many services an outside-access sentence names before it says "and N more" (Details lists them all). */
const NAMED_HOSTS = 3;

/** An App's outside-access request as one sentence: what it may do, where, in the person's language. A decided
 * request reads as what is now true. Null when the request is not one (the generic summary applies). */
export function outsideAccessSummary(
  action: ActionView,
  copy: AppApprovalsCopy,
  locale: string,
): string | null {
  if (action.tool !== 'approve_app_outside_access') return null;
  const { title, hosts, access } = action.args;
  if (typeof title !== 'string' || !Array.isArray(hosts) || !hosts.length) return null;
  const named = hosts.length > NAMED_HOSTS ? hosts.slice(0, NAMED_HOSTS - 1) : hosts;
  const items = named.map(String);
  if (hosts.length > named.length)
    items.push(formatCopy(copy.outsideMore, { count: String(hosts.length - named.length) }));
  let list = items.join(', ');
  try {
    list = new Intl.ListFormat(locale, { type: 'conjunction' }).format(items);
  } catch {
    /* The plain list stays. */
  }
  const done = action.status === 'executed';
  const key =
    access === 'none'
      ? done
        ? 'outsideNoneDone'
        : 'outsideNone'
      : access === 'read_send'
        ? done
          ? 'outsideReadSendDone'
          : 'outsideReadSend'
        : done
          ? 'outsideReadDone'
          : 'outsideRead';
  return formatCopy(copy[key], { app: title.trim(), hosts: list });
}

const EVENT_CHANGE_WORDS = {
  insert: 'eventInsert',
  update: 'eventUpdate',
  delete: 'eventDelete',
} as const;

/** An App event switch as one sentence: what starts the App's function and what it runs, in the person's
 * language. A decided switch reads as what is now true. Null when the action is not one. */
export function appEventSummary(
  action: ActionView,
  copy: AppApprovalsCopy,
  locale: string,
): string | null {
  if (action.tool !== 'set_app_event') return null;
  const { title, event, kind, collection, changes, runs, enabled } = action.args;
  if (typeof title !== 'string' || typeof runs !== 'string' || typeof enabled !== 'boolean')
    return null;
  const done = action.status === 'executed';
  if (kind === 'webhook' && typeof event === 'string') {
    const key = enabled
      ? done
        ? 'eventWebhookOnDone'
        : 'eventWebhookOn'
      : done
        ? 'eventWebhookOffDone'
        : 'eventWebhookOff';
    return formatCopy(copy[key], { app: title.trim(), event: event.trim() });
  }
  if (kind !== 'collection' || typeof collection !== 'string' || !Array.isArray(changes))
    return null;
  const words = changes
    .filter((item): item is keyof typeof EVENT_CHANGE_WORDS =>
      Object.hasOwn(EVENT_CHANGE_WORDS, item),
    )
    .map((item) => copy[EVENT_CHANGE_WORDS[item]]);
  let list = words.join(', ');
  try {
    list = new Intl.ListFormat(locale, { type: 'disjunction' }).format(words);
  } catch {
    /* The plain list stays. */
  }
  const key = enabled
    ? done
      ? 'eventRowsOnDone'
      : 'eventRowsOn'
    : done
      ? 'eventRowsOffDone'
      : 'eventRowsOff';
  return formatCopy(copy[key], {
    app: title.trim(),
    runs: spokenFunction(runs),
    collection,
    changes: list,
  });
}

const SCHEDULE_SENTENCES = {
  turn_on: ['scheduleTurnOn', 'scheduleTurnOnDone'],
  resume: ['scheduleResume', 'scheduleResumeDone'],
  reschedule: ['scheduleReschedule', 'scheduleRescheduleDone'],
  pause: ['schedulePause', 'schedulePauseDone'],
} as const;
// A card that moves the schedule to the latest version says so, whether it resumes it, and whether it is left
// paused.
const SCHEDULE_SWITCH_SENTENCES = {
  latest_saved: {
    on: ['scheduleSwitchSaved', 'scheduleSwitchSavedDone'],
    resume: ['scheduleResumeSaved', 'scheduleResumeSavedDone'],
    paused: ['scheduleSwitchSavedPaused', 'scheduleSwitchSavedPausedDone'],
  },
  published: {
    on: ['scheduleSwitchPublished', 'scheduleSwitchPublishedDone'],
    resume: ['scheduleResumePublished', 'scheduleResumePublishedDone'],
    paused: ['scheduleSwitchPublishedPaused', 'scheduleSwitchPublishedPausedDone'],
  },
} as const;

/** The sentence pair (pending, decided) for a schedule card, from its change, the state it leaves and the
 * version it moves to; null when the card is not one. */
function scheduleSentences(args: Record<string, unknown>) {
  const ends = args.state === 'paused' ? 'paused' : 'on';
  const { change, version } = args;
  if (typeof version === 'string' && Object.hasOwn(SCHEDULE_SWITCH_SENTENCES, version))
    return SCHEDULE_SWITCH_SENTENCES[version as keyof typeof SCHEDULE_SWITCH_SENTENCES][
      change === 'resume' && ends === 'on' ? 'resume' : ends
    ];
  // A new cadence for a schedule that is (or will be) paused says it stays paused.
  if (change === 'reschedule' && ends === 'paused')
    return ['scheduleReschedulePaused', 'scheduleReschedulePausedDone'] as const;
  return typeof change === 'string' && Object.hasOwn(SCHEDULE_SENTENCES, change)
    ? SCHEDULE_SENTENCES[change as keyof typeof SCHEDULE_SENTENCES]
    : null;
}

/** A schedule's runs to come as one sentence in the person's language, as the Versions panel words them: the
 * first in full, the later ones as briefly as they read clearly, all in the schedule's own zone. Null when
 * there are none to show. */
export function scheduleNextWords(
  runs: readonly string[],
  zone: string | undefined,
  copy: AppApprovalsCopy,
  locale: string,
) {
  const [first, ...later] = runs;
  const head = first ? formatMoment(first, locale, zone) : null;
  if (!head) return null;
  const words = [head, ...laterRunWords(later, first, locale, zone)];
  let list: string;
  try {
    list = new Intl.ListFormat(locale, { type: 'conjunction' }).format(words);
  } catch {
    list = words.join(', ');
  }
  const sentence = formatCopy(copy.scheduleNext, { runs: list });
  // A date that ends in its own full stop (“26 okt.”) already ends the sentence.
  return sentence.endsWith('..') ? sentence.slice(0, -1) : sentence;
}

/** A schedule's runs to come as the switch returned them, while it is on. */
function nextRunWords(result: unknown, copy: AppApprovalsCopy, locale: string) {
  const schedule = (
    result as {
      appSchedule?: {
        state?: unknown;
        cadence?: { timezone?: unknown; nextRuns?: unknown } | null;
      };
    } | null
  )?.appSchedule;
  const runs = schedule?.cadence?.nextRuns;
  if (schedule?.state !== 'on' || !Array.isArray(runs)) return null;
  const zone =
    typeof schedule.cadence?.timezone === 'string' ? schedule.cadence.timezone : undefined;
  return scheduleNextWords(
    runs.filter((at): at is string => typeof at === 'string'),
    zone,
    copy,
    locale,
  );
}

/** An App schedule change as whole sentences in the person's language: what changes (and, when it moves the
 * schedule to the latest version, that move), then its cadence in the schedule panel's own words, and
 * whether it is left paused. A decided change reads as what is now true, with its next runs. Null when the
 * action is not one. */
export function appScheduleSummary(
  action: ActionView,
  copy: AppApprovalsCopy,
  tracker: Readonly<Record<AppTrackerCopyKey, string>>,
  locale: string,
): string | null {
  if (action.tool !== 'set_app_schedule') return null;
  const { title, job, change, timezone } = action.args;
  if (typeof title !== 'string' || typeof job !== 'string' || typeof change !== 'string')
    return null;
  const sentences = scheduleSentences(action.args);
  if (!sentences) return null;
  const cadence = parseAppSchedulePresentation(action.args.cadence);
  if (!cadence && change !== 'pause') return null;
  const done = action.status === 'executed';
  const text = formatCopy(copy[sentences[done ? 1 : 0]], {
    app: title.trim(),
    job: job.trim(),
    cadence: cadence ? scheduleWords(cadence, tracker, locale) : '',
    timezone: typeof timezone === 'string' ? timezone : (cadence?.timezone ?? ''),
  });
  const next = done ? nextRunWords(action.result, copy, locale) : null;
  return next ? `${text} ${next}` : text;
}

const DATA_WRITE_WORDS = {
  insert: 'dataWriteInsert',
  update: 'dataWriteUpdate',
  delete: 'dataWriteDelete',
} as const;

/** An App function's own-row writes as one sentence, in the person's language. A decided switch reads as
 * what is now true. Null when the action is not one. */
export function appDataWritesSummary(
  action: ActionView,
  copy: AppApprovalsCopy,
  locale: string,
): string | null {
  if (action.tool !== 'set_app_data_writes') return null;
  const { title, function: name, changes, allowed } = action.args;
  if (typeof title !== 'string' || typeof name !== 'string' || typeof allowed !== 'boolean')
    return null;
  const done = action.status === 'executed';
  if (!allowed)
    return formatCopy(copy[done ? 'dataWritesOffDone' : 'dataWritesOff'], {
      app: title.trim(),
      function: spokenFunction(name.trim()),
    });
  if (!Array.isArray(changes)) return null;
  const words = changes
    .filter((item): item is keyof typeof DATA_WRITE_WORDS => Object.hasOwn(DATA_WRITE_WORDS, item))
    .map((item) => copy[DATA_WRITE_WORDS[item]]);
  if (!words.length) return null;
  let verbs = words.join(', ');
  try {
    verbs = new Intl.ListFormat(locale, { type: 'conjunction' }).format(words);
  } catch {
    /* The plain list stays. */
  }
  return formatCopy(copy[done ? 'dataWritesOnDone' : 'dataWritesOn'], {
    app: title.trim(),
    function: spokenFunction(name.trim()),
    verbs,
  });
}

const TABLE_CHANGE_SENTENCES = {
  drop_field: ['tableDropField', 'tableDropFieldDone'],
  drop_table: ['tableDropTable', 'tableDropTableDone'],
  clear_values: ['tableClear', 'tableClearDone'],
  restore_field: ['tableRestoreField', 'tableRestoreFieldDone'],
  restore_table: ['tableRestoreTable', 'tableRestoreTableDone'],
} as const;

// How many rows, in the person's language: rows that lose a value when a field's values go, else rows.
function tableRows(
  rows: number,
  losing: boolean,
  done: boolean,
  copy: AppApprovalsCopy,
  locale: string,
) {
  let count = String(rows);
  try {
    count = new Intl.NumberFormat(locale).format(rows);
  } catch {
    /* The plain number stays. */
  }
  const one = rows === 1;
  const key = losing
    ? done
      ? one
        ? 'tableRowsLostOne'
        : 'tableRowsLostMany'
      : one
        ? 'tableRowsLoseOne'
        : 'tableRowsLoseMany'
    : one
      ? 'tableRowsOne'
      : 'tableRowsMany';
  return formatCopy(copy[key], { count });
}

/** A change to an App's tables as one sentence, in the person's language: the App, what goes or comes back,
 * where, and how many rows. A decided change reads as what is now true. Null when the action is not one. */
export function appTableChangeSummary(
  action: ActionView,
  copy: AppApprovalsCopy,
  locale: string,
): string | null {
  if (action.tool !== 'apply_app_table_change') return null;
  const { title, change, restores, table, field, rows } = action.args;
  if (typeof title !== 'string' || typeof table !== 'string' || typeof rows !== 'number')
    return null;
  const kind =
    change === 'restore' ? (restores === 'drop_table' ? 'restore_table' : 'restore_field') : change;
  if (typeof kind !== 'string' || !Object.hasOwn(TABLE_CHANGE_SENTENCES, kind)) return null;
  const sentence = TABLE_CHANGE_SENTENCES[kind as keyof typeof TABLE_CHANGE_SENTENCES];
  const needsField = kind !== 'drop_table' && kind !== 'restore_table';
  if (needsField && typeof field !== 'string') return null;
  const done = action.status === 'executed';
  const losing = kind === 'drop_field' || kind === 'clear_values';
  return formatCopy(copy[sentence[done ? 1 : 0]], {
    app: title.trim(),
    table: table.trim(),
    field: typeof field === 'string' ? field.trim() : '',
    rows: tableRows(rows, losing, done, copy, locale),
  });
}

/** An App function's connected write as one sentence: the App, the action and the company system. */
export function appConnectedWriteSummary(
  action: ActionView,
  copy: AppApprovalsCopy,
): string | null {
  if (action.tool !== 'set_app_connected_write') return null;
  const { title, action: verb, destination, enabled } = action.args;
  if ([title, verb, destination].some((value) => typeof value !== 'string')) return null;
  const done = action.status === 'executed';
  const key = enabled
    ? done
      ? 'connectedWriteOnDone'
      : 'connectedWriteOn'
    : done
      ? 'connectedWriteOffDone'
      : 'connectedWriteOff';
  return formatCopy(copy[key], {
    app: String(title).trim(),
    action: String(verb).trim(),
    destination: String(destination).trim(),
  });
}

// app.json names are identifiers; a card reads them as words.
const spokenFunction = (value: unknown) => String(value).replaceAll('_', ' ');

/** A saved App function that changes data, run from chat, as one sentence. */
export function appRunSummary(action: ActionView, copy: AppApprovalsCopy) {
  if (action.tool !== 'run_app_function_with_changes') return null;
  const { title, function: name } = action.args;
  if (typeof title !== 'string' || typeof name !== 'string') return null;
  const key = action.status === 'executed' ? 'runChangesDone' : 'runChanges';
  return formatCopy(copy[key], { app: title.trim(), function: spokenFunction(name) });
}

/** Changed code the owner lets use their approvals, as one sentence. */
export function appCodeSummary(action: ActionView, copy: AppApprovalsCopy, locale: string) {
  if (action.tool !== 'confirm_app_code') return null;
  const { title, changedBy } = action.args;
  if (typeof title !== 'string' || !Array.isArray(changedBy)) return null;
  const done = action.status === 'executed';
  // No one else is named (or the history is too long to list): the card confirms the App's current code.
  if (!changedBy.length)
    return formatCopy(copy[done ? 'codeConfirmCurrentDone' : 'codeConfirmCurrent'], {
      app: title.trim(),
    });
  const items = changedBy.map(String);
  let names = items.join(', ');
  try {
    names = new Intl.ListFormat(locale, { type: 'conjunction' }).format(items);
  } catch {
    /* The plain list stays. */
  }
  return formatCopy(copy[done ? 'codeConfirmDone' : 'codeConfirm'], { app: title.trim(), names });
}

export const displayFields = (action: ActionView, copy?: ActionCopy) =>
  (ACTION_FIELDS[action.tool] ?? Object.keys(action.args).filter((key) => !privateActionField(key)))
    .filter((key) => !privateActionField(key) && Object.hasOwn(action.args, key))
    .map((key) => [key, presented(action, key, copy?.words ?? ENGLISH_WORDS)] as const)
    // A connection that lists no resources shows no Resources row; changed code nobody is named for no Changed by row.
    .filter(
      ([key, value]) =>
        !(action.tool === 'update_work_connection' && key === 'resourceLabels' && value === null) &&
        !(action.tool === 'confirm_app_code' && key === 'changedBy' && value === null),
    );

// Projects v1 fields, in English until the project copy namespace exists.
const PROJECT_FIELDS: Record<string, string> = {
  project: 'Project',
  brief: 'Brief',
  followsSkills: 'Skills every chat follows',
  endsOn: 'Ends',
  followsSkillIds: 'Skills every chat follows',
  readsFirst: 'Reads first',
};

export function readableKey(key: string, copy: ActionCopy) {
  return copy.fields[key] ?? PROJECT_FIELDS[key] ?? copy.fields.content ?? copy.review;
}

export function actionSummary(
  action: ActionView,
  copy: ActionCopy,
  chrome: ChatCopy,
  withdraw: string,
  storefront?: { design: string; knowledge: string; read?: (action: ActionView) => string | null },
  named?: (action: ActionView) => string | null,
): string {
  if (action.tool === 'withdraw_pending_proposal') return withdraw;
  const sentence = named?.(action);
  if (sentence) return sentence;
  if (storefront && action.tool === 'change_storefront_design') return storefront.design;
  if (storefront && action.tool === 'change_storefront_knowledge') return storefront.knowledge;
  const read = storefront?.read?.(action);
  if (read) return read;
  // The native build is named by the exact request the person is approving, never a derived title.
  if (action.tool === 'build_artifact' && typeof action.args.task === 'string')
    return action.args.task;
  if (action.tool === 'set_chat_model') {
    const model = MODEL_CHOICE_COPY[action.args.choice as ModelChoice]?.name ?? copy.review;
    return `${copy.fields.chat_model_choice}: ${model}`;
  }
  if (action.tool === 'update_my_settings') {
    const themes: Record<string, string> = {
      light: chrome.themeLight,
      dark: chrome.themeDark,
      system: chrome.themeSystem,
    };
    const values = displayFields(action, copy).map(([key, value]) => {
      const label =
        key === 'theme'
          ? themes[String(value)]
          : typeof value === 'object' && value !== null
            ? Object.entries(value)
                .map(
                  ([name, item]) =>
                    `${readableKey(name, copy)}: ${typeof item === 'object' ? JSON.stringify(item) : String(item)}`,
                )
                .join(', ')
            : String(value ?? '—');
      return `${readableKey(key, copy)}: ${label ?? '—'}`;
    });
    return values.join(' · ');
  }
  // Exact type metadata supplies the localized action and a bounded subject.
  const subjectKey = (ACTION_FIELDS[action.tool] ?? Object.keys(action.args)).find((key) =>
    ['name', 'title', 'email', 'flow', 'text', 'content', 'instructions', 'markdown'].includes(key),
  );
  const subject =
    subjectKey && typeof action.args[subjectKey] === 'string' ? action.args[subjectKey].trim() : '';
  const bounded = subject.length > 96 ? `${subject.slice(0, 93)}…` : subject;
  const kind = copy.types[action.tool as ActionKind];
  const base = kind ?? copy.review;
  return bounded ? `${base}: ${bounded}` : base;
}

export function useActionSummary() {
  const chrome = useChatCopy();
  const locale = useChatInterfaceLocale();
  const copy = actionCopy(locale);
  const knowledge = useNamespaceCopy(meetingsKnowledgeCopy);
  const storefront = useNamespaceCopy(storefrontCopy);
  const apps = useNamespaceCopy(appApprovalsCopy);
  const tracker = useNamespaceCopy(appTrackerCopy);
  const titles = {
    design: storefront.actionDesignTitle,
    knowledge: storefront.actionKnowledgeTitle,
    // A paid read is named by what it reads: "Read the products again", "Add a market".
    read: (action: ActionView) => readTitle(storefront, action.tool, action.args),
  };
  return (action: ActionView) =>
    actionSummary(
      action,
      copy,
      chrome,
      knowledge.withdraw,
      titles,
      (candidate) =>
        outsideAccessSummary(candidate, apps, locale) ??
        appEventSummary(candidate, apps, locale) ??
        appScheduleSummary(candidate, apps, tracker, locale) ??
        appDataWritesSummary(candidate, apps, locale) ??
        appTableChangeSummary(candidate, apps, locale) ??
        appConnectedWriteSummary(candidate, apps) ??
        appCodeSummary(candidate, apps, locale) ??
        appRunSummary(candidate, apps),
    );
}

export function statusLabel(status: ActionView['status'], copy: ActionCopy) {
  if (status === 'pending') return copy.pending;
  if (status === 'running') return copy.running;
  if (status === 'executed') return copy.approved;
  if (status === 'rejected') return copy.rejected;
  if (status === 'superseded') return copy.superseded;
  if (status === 'failed') return copy.failed;
  return copy.expired;
}
